import { useRef, useState } from "react";

import API from "../Global/API";
import { useAuth } from "../Global/Auth";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { toBlob } from "html-to-image";
import { useNavigate } from "react-router";
import Settings from "../Global/Settings";
import State from "../Global/State";

import DirectDepositStep from "../Pages/Employee/HirePacket/DirectDepositStep";
import EmploymentEligibilityStep from "../Pages/Employee/HirePacket/EmploymentEligibilityStep";
import JobDescriptionStep from "../Pages/Employee/HirePacket/JobDescriptionStep";
import ResidencyDisclosureStep from "../Pages/Employee/HirePacket/ResidencyDisclosureStep";
import W4CertificateStep from "../Pages/Employee/HirePacket/W4CertificateStep";
import JobInterviewStep from "../Pages/Employee/Invitation/JobInterviewStep";
import ReferenceStep from "../Pages/Employee/Invitation/ReferenceStep";
import ProfileForm from "../Pages/Employee/Profile/ProfileForm";
import TrainingOverview from "../Pages/Employee/Profile/TrainingOverview";

const EmployeeSyncActions = (props) => {
    const {
        record, // {Object} The employee record / context.
        onClose, // {Function} An optional close handler for the parent layout.
        doReload, // {Function} Reloads the parent layout, if applicable.
    } = props;

    const { id } = record;
    const navigate = useNavigate();
    const previewRef = useRef(null);
    const { hasPermissionTo } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState(null);

    const uploadPath = `employees/${record.id}/documents`;

    /**
     * Indicates whether we have an anchor element.
     *
     * @type {boolean}
     */
    const open = Boolean(anchorEl);

    /**
     * Fired whenever the menu button is clicked.
     *
     * @param event
     */
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * Closes the menu popover.
     */
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDetailsSync = async (popup) => {
        if (popup) {
            handleClose();
            setPopupVisible(true);
        }

        const request = {
            id: record.id,
        };

        const response = await API.post("helixDetailSync", request);
        console.log(response);

        if (popup) setPopupVisible(false);
        return null;
    };

    const handleDocSync = async (popup) => {
        if (popup) {
            handleClose();
            setPopupVisible(true);
        }

        const request = {
            id: record.id,
        };

        const response = await API.post("helixSyncAll", request);
        console.log(response);

        if (popup) setPopupVisible(false);
        return null;
    };

    const PreviewContent = () => {
        return (
            <Box
                ref={previewRef}
                style={{
                    position: "fixed",
                    top: "0px",
                    left: "0px",
                    backgroundColor: "white",
                    opacity: "0%",
                    padding: "20px",
                    maxWidth: "800px",
                    PointerEvent: "none",
                }}
            >
                {previewContent}
            </Box>
        );
    };

    const waitForRender = async () => {
        return new Promise((resolve) => {
            requestAnimationFrame(() => {
                setTimeout(resolve, 0);
            });
        });
    };

    const getUniqueFilename = (files, desiredFilename, ext) => {
        const existingFilenames = new Set(files.map((obj) => obj.key));

        // If the desired filename doesn't exist, return it as-is
        if (!existingFilenames.has(`${uploadPath}/${desiredFilename}.${ext}`)) {
            return desiredFilename;
        }

        // Otherwise, find the next available suffix number
        let counter = 1;
        let newFilename;

        do {
            newFilename = `${desiredFilename}_${counter}`;
            counter++;
        } while (existingFilenames.has(`${uploadPath}/${newFilename}.${ext}`));

        return newFilename;
    };

    const doFileUploadPromise = (file, parameters) => {
        return new Promise((resolve, reject) => {
            API.doFileUpload(
                parameters,
                (result) => {
                    console.log(`✓ ${file.name} uploaded successfully`);
                    resolve(result);
                },
                (error) => {
                    console.error(`✗ ${file.name} failed:`, error);
                    reject(error);
                },
                () => {}, // onProgress - just ignore it
            );
        });
    };

    const fetchAndUploadFiles = async () => {
        const files = await fetchDocsToSync();

        // Clear the preview content
        setPreviewContent(null);

        const uploads = files.map((file) =>
            doFileUploadPromise(file, {
                file: file,
                path: uploadPath,
                types: [],
            }),
        );

        await Promise.all(uploads);

        return files;
    };

    const handleQuestionnaireSync = async () => {
        handleClose();
        setPopupVisible(true);

        const files = await fetchAndUploadFiles();

        // Step 4: Initiate document sync via API call
        const documents = files.map((file) => `${uploadPath}/${file.name}`);

        const request = {
            id: record.id,
            documents: documents,
        };

        const response = await API.post("helixSync", request);
        console.log(response);

        setPopupVisible(false);
    };

    const fetchDocsToSync = async () => {
        const pngDocList = [
            <DirectDepositStep
                readOnly
                employee={record}
                settingsPrefix={"HP_DD_"}
            />,
            <TrainingOverview employee={record} />,
            <JobDescriptionStep
                readOnly
                employee={record}
                specialty={record.specialty || {}}
                settingsPrefix={"HP_JOB"}
            />,
            <ResidencyDisclosureStep
                readOnly
                employee={record}
                settingsPrefix={"HP_RD_"}
            />,
            <Box className={"pt__0"}>
                <ResidencyDisclosureStep
                    readOnly
                    employee={record}
                    settingsPrefix={"HP_RD_"}
                />
                <W4CertificateStep
                    readOnly
                    employee={record}
                    settingsPrefix={"HP_W4_"}
                />
                <DirectDepositStep
                    readOnly
                    employee={record}
                    settingsPrefix={"HP_DD_"}
                />
                <EmploymentEligibilityStep
                    readOnly
                    employee={record}
                    settingsPrefix={"HP_EE_"}
                />
            </Box>,
            <ReferenceStep readOnly employee={record} />,
            <Box>
                <ProfileForm readOnly employee={record} />
                <JobInterviewStep readOnly employee={record} />
                <ReferenceStep readOnly employee={record} />
            </Box>,
        ];

        const pngFileNames = [
            "Direct Deposit",
            "Annual Training",
            "Job Description",
            "Residency Disclosure",
            "Hire Packet",
            "References",
            "Application",
        ];

        const pdfDocList = [
            `${Settings.apiPath}/pdf/i9?employeeId=${record.id}&x-access-token=${State.get("auth-token")}`,
            `${Settings.apiPath}/pdf/w4?employeeId=${record.id}&x-access-token=${State.get("auth-token")}`,
        ];

        const pdfFileNames = ["I9", "W4"];

        const existingDocs = await API.get(`files?path=${uploadPath}`);
        const files = [];

        for (let i = 0; i < pngDocList.length; i++) {
            const doc = pngDocList[i];
            const name = pngFileNames[i];

            // Set the current document to preview
            setPreviewContent(doc);
            await waitForRender();
            await new Promise((r) => setTimeout(r, 2000));

            // Capture the preview
            if (previewRef.current) {
                try {
                    const dataBlob = await toBlob(previewRef.current, {
                        style: {
                            opacity: "100%",
                        },
                    });

                    const disambiguatedName = getUniqueFilename(
                        existingDocs,
                        `${name}-${new Date().toISOString().split("T")[0]}`,
                        "png",
                    );

                    files.push(
                        new File([dataBlob], `${disambiguatedName}.png`, {
                            type: "image/png",
                        }),
                    );

                    console.log(
                        `Captured document ${i + 1}/${pngDocList.length}`,
                    );
                } catch (error) {
                    console.error(
                        `Failed to capture document ${i + 1}:`,
                        error,
                    );
                }
            }
        }

        for (let i = 0; i < pdfDocList.length; i++) {
            const docUrl = pdfDocList[i];
            const name = pdfFileNames[i];

            let blob;
            let mime;

            try {
                const response = await fetch(docUrl);
                if (response.status !== 200)
                    throw new Error(`HTTP ${response.status}`);

                mime = response.headers.get("content-type");
                blob = await response.blob();
            } catch (error) {
                console.error("Download Failed:", error);
                continue;
            }
            
            if (!mime || mime.trim().length === 0 || mime === "application/json") {
                console.error(`Could not download ${name}, result was blank/json`);
                continue;
            }

            // Disambiguate filename with the file type and current date.
            let filename = getUniqueFilename(
                existingDocs,
                `${name}-${new Date().toISOString().split("T")[0]}`,
                "pdf",
            );
            files.push(new File([blob], `${filename}.pdf`, { type: mime }));
        }

        return files;
    };

    return (
        <Box>
            <Box key={`EmployeeSyncActions-${id}__record`}>
                <IconButton onClick={handleClick}>
                    <MoreHorizIcon />
                </IconButton>
                <Menu open={open} onClose={handleClose} anchorEl={anchorEl}>
                    <MenuItem
                        onClick={() => handleDetailsSync(true)}
                        children={"Sync Employee Details"}
                        disabled={!record.status || (record.status.name !== "Ready to Go" &&
                            record.status.name !== "Active")}
                    />

                    <MenuItem
                        onClick={() => handleQuestionnaireSync()}
                        children={"Sync Questionnaires"}
                        disabled={!record.helixId ||
                            !(record.status.name === "Ready to Go" ||
                                record.status.name === "Active")}
                    />

                    <MenuItem
                        onClick={() => handleDocSync(true)}
                        children={"Sync Documents"}
                        disabled={!record.helixId ||
                            !(record.status.name === "Ready to Go" ||
                                record.status.name === "Active")}
                    />
                </Menu>
            </Box>

            <PreviewContent />

            <Box
                style={{
                    ...styles.overlay,
                    display: popupVisible ? "flex" : "none",
                    color: "white",
                }}
            >
                <h2>Document Sync in progress.</h2>
                <h2>Please do not close the page.</h2>
            </Box>
        </Box>
    );
};

const styles = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
    },
};

export default EmployeeSyncActions;
